// Shared chat-context predicates for the one-chat shell.
//
// Pitch chats and agent-tender response chats both live under the `pitch-<id>`
// cid namespace, so every club-side pitch module used to gate on nothing more
// than the string `pitch-` appearing in the cid. That over-mounts the club rail,
// the pitch tender card and the club-admin menu items onto agent-tender response
// chats, which are anonymous agent↔agent threads (tp_context agent_agent) and
// must render as a plain, timerless shell.
//
// The discriminator is `channel.data.tender_source === "agent"`: it is stamped
// ONLY by the agent-tender response path (create_agent_response_channel) and is
// never present on genuine club↔agent pitch chats. See
// hooks/inbox/useInbox.js classifyInboxChannel for the same signal.

// True when the channel identifies as a tender pitch — the numeric pitch id in
// the cid, or an explicit pitch_id / pitch_details on the channel data. Mirrors
// the union of the checks the three call sites used before this predicate.
export const hasPitchIdentity = (channel) => {
  if (!channel) return false;
  if (channel.data?.pitch_id) return true;
  if (channel.data?.pitch_details?.pitch_id) return true;
  if (channel.data?.pitch_details?.id) return true;
  const source = String(channel.cid || channel.id || channel.data?.id || "");
  return source.includes("pitch-");
};

// True for agent-tender board response chats (anon agent↔agent). These reuse the
// `pitch-<id>` cid but must NOT mount the club-side pitch modules.
export const isAgentTenderChat = (channel) =>
  channel?.data?.tender_source === "agent";

// True only for genuine club↔agent pitch chats — the ones that legitimately
// carry the club rail, the pitch tender card and the club-admin menu items.
export const isClubAgentPitch = (channel) =>
  hasPitchIdentity(channel) && !isAgentTenderChat(channel);
